/* @flow */
// Reads the text out of a Word file (.docx) in the browser, so the file never
// has to be uploaded: a .docx is a zip, and the words live in word/document.xml.
// Plain .txt files are read as they are.

const textDecoder = () => new TextDecoder('utf-8')

const u16 = (b, i) => b[i] | (b[i + 1] << 8)
const u32 = (b, i) => (b[i] | (b[i + 1] << 8) | (b[i + 2] << 16) | (b[i + 3] << 24)) >>> 0

// Finds a file inside the zip and returns its bytes (stored or deflated)
async function unzipEntry (bytes: Uint8Array, wanted: string): Promise<Uint8Array | null> {
  // End of central directory record: signature 0x06054b50, search from the end
  let eocd = -1
  for (let i = bytes.length - 22; i >= Math.max(0, bytes.length - 70000); i--) {
    if (u32(bytes, i) === 0x06054b50) { eocd = i; break }
  }
  if (eocd < 0) throw new Error('not a zip')
  const count = u16(bytes, eocd + 10)
  let p = u32(bytes, eocd + 16)
  for (let n = 0; n < count; n++) {
    if (u32(bytes, p) !== 0x02014b50) break
    const method = u16(bytes, p + 10)
    const compSize = u32(bytes, p + 20)
    const nameLen = u16(bytes, p + 28)
    const extraLen = u16(bytes, p + 30)
    const commentLen = u16(bytes, p + 32)
    const localOffset = u32(bytes, p + 42)
    const name = textDecoder().decode(bytes.subarray(p + 46, p + 46 + nameLen))
    if (name === wanted) {
      const lNameLen = u16(bytes, localOffset + 26)
      const lExtraLen = u16(bytes, localOffset + 28)
      const start = localOffset + 30 + lNameLen + lExtraLen
      const data = bytes.subarray(start, start + compSize)
      if (method === 0) return data
      if (method === 8) {
        if (typeof DecompressionStream === 'undefined') throw new Error('This browser cannot read Word files. Paste the text instead.')
        const stream = new Blob([data]).stream().pipeThrough(new DecompressionStream('deflate-raw'))
        return new Uint8Array(await new Response(stream).arrayBuffer())
      }
      throw new Error('unsupported zip method')
    }
    p += 46 + nameLen + extraLen + commentLen
  }
  return null
}

// Word XML to plain text: paragraphs become lines, tabs become spaces
function wordXmlToText (xml: string): string {
  return xml
    .replace(/<w:tab\/>/g, ' ')
    .replace(/<\/w:p>/g, '\n')
    .replace(/<w:br[^>]*\/>/g, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&apos;|&#39;/g, "'")
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

export async function readDocumentFile (file: File): Promise<string> {
  const name = (file.name || '').toLowerCase()
  if (name.endsWith('.txt') || name.endsWith('.md') || file.type === 'text/plain') {
    return (await file.text()).trim()
  }
  if (name.endsWith('.docx')) {
    const bytes = new Uint8Array(await file.arrayBuffer())
    const xml = await unzipEntry(bytes, 'word/document.xml')
    if (!xml) throw new Error("That doesn't look like a Word document.")
    return wordXmlToText(textDecoder().decode(xml))
  }
  if (name.endsWith('.doc')) {
    throw new Error('Old .doc files are not supported. Save it as .docx in Word, or paste the text.')
  }
  throw new Error('Choose a Word (.docx) or text (.txt) file, or paste the text.')
}
